// "Analyse failure": asks the start_rca Lambda to run the pipeline-rca workflow
// for a failed Argo deployment, and reports the outcome in a snackbar. Renders
// nothing for a record the Lambda would refuse (see canStartRca). The result
// itself arrives later on the record's `rca` map, over the live stream.

import React, { useState } from 'react';
import { Alert, Button, IconButton, Snackbar, Tooltip } from '@mui/material';
import { Psychology as AnalyseIcon } from '@mui/icons-material';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { canStartRca, rcaInFlight, rcaStartMessage } from '../lib/rca';

export interface StartRcaButtonProps {
  record: DeploymentRecord;
  /** `icon` for a row's action group, `button` for a dialog. */
  variant?: 'icon' | 'button';
}

interface Feedback {
  severity: 'success' | 'info' | 'error';
  text: string;
}

const StartRcaButton: React.FC<StartRcaButtonProps> = ({ record, variant = 'icon' }) => {
  const { startRca } = useDeploymentData();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  if (!canStartRca(record)) return null;

  const inFlight = rcaInFlight(record);
  const disabled = busy || inFlight;
  const tooltip = inFlight ? 'An analysis is already running' : 'Analyse failure';

  const start = async (e: React.MouseEvent) => {
    // Rows and history entries open the details dialog on click — don't do both.
    e.stopPropagation();
    setBusy(true);
    try {
      const result = await startRca(record.deployment_id || '');
      setFeedback({
        severity: result.status === 'already_running' ? 'info' : 'success',
        text: rcaStartMessage(result),
      });
    } catch (err) {
      setFeedback({ severity: 'error', text: (err as Error).message || 'Failed to start the analysis' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* The span keeps the tooltip working on a disabled button. */}
      <Tooltip title={tooltip} arrow>
        <span>
          {variant === 'icon' ? (
            <IconButton size="small" aria-label="Analyse failure" disabled={disabled} onClick={start}>
              <AnalyseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={start}
              startIcon={<AnalyseIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Analyse failure
            </Button>
          )}
        </span>
      </Tooltip>
      <Snackbar
        open={!!feedback}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClick={e => e.stopPropagation()}
      >
        {feedback ? (
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)} variant="filled">
            {feedback.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default StartRcaButton;
